import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Popup } from '@atlaskit/popup/popup';
import Toggle from '@atlaskit/toggle/toggle';

import { CONNECTION_MESSAGE } from './connectionMessages.ts';
import { ConnectionLogo } from './ProductLogos.tsx';
import {
	type ExternalProductConnection,
	getConnectionForProviderId,
} from './__types__/RemoteObject.ts';

const i18n = defineMessages({
	filterButtonLabel: {
		id: 'space-curation.review-content-suggestions-modal.filter-button-label',
		defaultMessage: 'Filter suggestions by source',
		description:
			'Accessible label for the settings button that opens the suggestions source filter popup',
	},
	showOnlyFrom: {
		id: 'space-curation.review-content-suggestions-modal.filter-show-only-from',
		defaultMessage: 'Show only suggestions from',
		description: 'Heading at the top of the suggestions source filter popup',
	},
});

const styles = cssMap({
	popupContent: {
		padding: token('space.075'),
		width: '260px',
	},
	sourceRow: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
	},
	heading: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
	},
	sourceName: {
		flexGrow: 1,
		minWidth: '0%',
	},
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		flexShrink: 0,
	},
});

export type EnabledSources = Record<string, boolean>;

type VisibleSource = {
	ari: string;
	connection: ExternalProductConnection;
};

type SuggestionsFilterPopupProps = {
	onClose: (sources: string[]) => void;
	availableSources: string[];
};

export const SuggestionsFilterPopup = ({
	onClose,
	availableSources,
}: SuggestionsFilterPopupProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	// Available sources are, by definition, ones the user already has suggestions from, so every
	// one is filterable — no auth/connect gating is needed here.
	const visibleSources = useMemo<VisibleSource[]>(
		() =>
			availableSources.reduce<VisibleSource[]>((acc, ari) => {
				const connection = getConnectionForProviderId(ari);
				if (connection) {
					acc.push({ ari, connection });
				}
				return acc;
			}, []),
		[availableSources],
	);
	const [enabledSources, setEnabledSources] = useState<EnabledSources>({});

	// A source counts as enabled unless it has been explicitly toggled off (missing entry ⇒ on).
	const isEnabled = useCallback(
		(source: VisibleSource): boolean => enabledSources[source.ari] !== false,
		[enabledSources],
	);

	const closePopup = useCallback(() => {
		setIsOpen(false);
		const enabled = visibleSources.filter(isEnabled);
		// All available sources enabled ⇒ no filter (empty list); otherwise submit the chosen ARIs.
		onClose(enabled.length === visibleSources.length ? [] : enabled.map((source) => source.ari));
	}, [onClose, isEnabled, visibleSources]);

	const handleToggle = (ari: string) => {
		setEnabledSources((prev) => ({ ...prev, [ari]: prev[ari] === false }));
	};

	const enabledCount = useMemo(
		() => visibleSources.reduce((count, source) => (isEnabled(source) ? count + 1 : count), 0),
		[isEnabled, visibleSources],
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={closePopup}
			placement="bottom-end"
			zIndex={800}
			content={() => (
				<Box xcss={styles.popupContent} testId="suggestions-filter-popup-content">
					<Stack space="space.0">
						<Box xcss={styles.heading}>
							<Text size="small" weight="bold" color="color.text.subtlest">
								{formatMessage(i18n.showOnlyFrom)}
							</Text>
						</Box>
						<Stack space="space.0">
							{visibleSources.map((source) => {
								const name = formatMessage(CONNECTION_MESSAGE[source.connection]);
								const checked = isEnabled(source);
								return (
									<Flex
										key={source.ari}
										alignItems="center"
										justifyContent="space-between"
										gap="space.100"
										xcss={styles.sourceRow}
									>
										<Inline alignBlock="center" space="space.100">
											<Box xcss={styles.iconWrapper}>
												<ConnectionLogo connection={source.connection} size="xxsmall" />
											</Box>
											<Box xcss={styles.sourceName}>
												<Text size="medium">{name}</Text>
											</Box>
										</Inline>
										<Toggle
											label={name}
											isChecked={checked}
											isDisabled={checked && enabledCount === 1}
											onChange={() => handleToggle(source.ari)}
											testId={`suggestions-filter-toggle-${source.connection}`}
										/>
									</Flex>
								);
							})}
						</Stack>
					</Stack>
				</Box>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					icon={CustomizeIcon}
					label={formatMessage(i18n.filterButtonLabel)}
					appearance="subtle"
					spacing="compact"
					isSelected={isOpen}
					onClick={() => (isOpen ? closePopup() : setIsOpen(true))}
					testId="suggestions-filter-button"
				/>
			)}
		/>
	);
};
